import { useEffect } from 'react';
import {
  Mail,
  MessageSquare,
  Trash2,
  Loader2,
  ArrowDownLeft,
  ArrowUpRight,
  ExternalLink,
  Bot,
  MapPinOff,
} from 'lucide-react';
import { useActivityStore } from '../../store/activityStore';
import { useAuthStore } from '../../store/authStore';
import type { Activity } from '../../types';
import toast from 'react-hot-toast';
import {
  ACTIVITY_ICONS,
  ACTIVITY_COLORS,
  EMAIL_DIRECTION_COLORS,
  MEETING_LOCATION_LABELS,
  formatRelativeTime,
  formatFullTimestamp,
  isEmailWithMetadata,
} from './activityVisuals';
import { gmailSearchUrl } from './gmailLink';

interface ActivityTimelineProps {
  personId: string;
}

// Email activity item — rich display with subject, snippet, direction, and Gmail link
const EmailActivityItem = ({
  activity,
  canDelete,
  onDelete,
  isDeleting,
  onOpen,
}: {
  activity: Activity;
  canDelete: boolean;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  onOpen: (id: string) => void;
}) => {
  const isInbound = activity.emailDirection === 'INBOUND';
  const DirectionIcon = isInbound ? ArrowDownLeft : ArrowUpRight;
  const directionColor = activity.emailDirection
    ? EMAIL_DIRECTION_COLORS[activity.emailDirection]
    : ACTIVITY_COLORS.EMAIL;
  const directionLabel = isInbound ? 'Received' : 'Sent';

  const gmailUrl = gmailSearchUrl(activity.rfc822MessageId);

  return (
    <div
      onClick={() => onOpen(activity.id)}
      className="flex gap-3 py-4 border-b border-gray-100 dark:border-gray-700 last:border-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-md"
    >
      {/* Icon with direction indicator */}
      <div className="relative flex-shrink-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${directionColor}`}>
          <Mail className="w-5 h-5" />
        </div>
        {activity.emailDirection && (
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800 ${
            isInbound
              ? 'bg-blue-500 text-white'
              : 'bg-indigo-500 text-white'
          }`}>
            <DirectionIcon className="w-3 h-3" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header: Direction label + badges + time */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {directionLabel} email
            </span>
            {activity.isAutoLogged && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                <Bot className="w-3 h-3" />
                auto-synced
              </span>
            )}
          </div>
          <span
            className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0"
            title={formatFullTimestamp(activity.activityAt)}
          >
            {formatRelativeTime(activity.activityAt)}
          </span>
        </div>

        {/* Subject line */}
        {activity.emailSubject && (
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
            {activity.emailSubject}
          </p>
        )}

        {/* Snippet */}
        {activity.emailSnippet && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
            {activity.emailSnippet}
          </p>
        )}

        {/* From/To addresses */}
        <div className="mt-1.5 space-y-0.5">
          {activity.emailFrom && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              <span className="font-medium">From:</span> {activity.emailFrom}
            </p>
          )}
          {activity.emailTo && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              <span className="font-medium">To:</span> {activity.emailTo}
            </p>
          )}
        </div>

        {/* Summary (for manually-logged emails that also have metadata, or fallback) */}
        {activity.summary && !activity.emailSnippet && (
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words mt-1">
            {activity.summary}
          </p>
        )}

        {/* Footer: View in Gmail + who logged it + delete */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-3">
            {gmailUrl && (
              <a
                href={gmailUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                View in Gmail
              </a>
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              by {activity.createdBy.displayName || activity.createdBy.email}
            </span>
          </div>

          {canDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(activity.id);
              }}
              disabled={isDeleting}
              className="text-xs text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
              title="Delete activity"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Standard activity item (non-email or manually logged email without metadata)
const ActivityItem = ({
  activity,
  canDelete,
  onDelete,
  isDeleting,
  onOpen,
}: {
  activity: Activity;
  canDelete: boolean;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  onOpen: (id: string) => void;
}) => {
  const Icon = ACTIVITY_ICONS[activity.type];
  const colorClass = ACTIVITY_COLORS[activity.type];

  return (
    <div
      onClick={() => onOpen(activity.id)}
      className="flex gap-3 py-4 border-b border-gray-100 dark:border-gray-700 last:border-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-md"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header: Type + Time */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
              {activity.type.toLowerCase().replace('_', ' ')}
            </span>
            {activity.meetingLocation && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400">
                {MEETING_LOCATION_LABELS[activity.meetingLocation] ?? activity.meetingLocation}
              </span>
            )}
            {activity.type === 'IN_PERSON' && activity.hasLocation === false && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                <MapPinOff className="w-3 h-3" />
                No location
              </span>
            )}
          </div>
          <span
            className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0"
            title={formatFullTimestamp(activity.activityAt)}
          >
            {formatRelativeTime(activity.activityAt)}
          </span>
        </div>

        {/* Summary */}
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
          {activity.summary}
        </p>

        {/* Footer: Who logged it + Delete */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            by {activity.createdBy.displayName || activity.createdBy.email}
          </span>

          {canDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(activity.id);
              }}
              disabled={isDeleting}
              className="text-xs text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
              title="Delete activity"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Activity timeline for a person
 */
export const ActivityTimeline = ({ personId }: ActivityTimelineProps) => {
  const {
    activities,
    pagination,
    isLoading,
    error,
    canEdit,
    isSaving,
    fetchActivities,
    setPage,
    deleteActivity,
    clearActivities,
    openActivityDetail,
  } = useActivityStore();
  const { user: currentUser } = useAuthStore();

  // Fetch activities on mount
  useEffect(() => {
    fetchActivities(personId);

    // Cleanup on unmount
    return () => {
      clearActivities();
    };
  }, [personId, fetchActivities, clearActivities]);

  // Handle delete
  const handleDelete = async (activityId: string) => {
    if (!confirm('Are you sure you want to delete this activity?')) {
      return;
    }

    try {
      await deleteActivity(personId, activityId);
      toast.success('Activity deleted');
    } catch (error) {
      // Error already shown via store
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    if (pagination && pagination.hasNextPage) {
      setPage(pagination.page + 1);
    }
  };

  // Loading state (initial load)
  if (isLoading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-gray-400 dark:text-gray-500 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        <button
          onClick={() => fetchActivities(personId)}
          className="mt-2 text-sm text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-400"
        >
          Try again
        </button>
      </div>
    );
  }

  // Empty state
  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageSquare className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400">No activities logged yet</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
          Log your first activity to start tracking interactions
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Activity list */}
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {activities.map((activity) => {
          const canDelete =
            canEdit || activity.createdBy.id === currentUser?.id;
          return isEmailWithMetadata(activity) ? (
            <EmailActivityItem
              key={activity.id}
              activity={activity}
              canDelete={canDelete}
              onDelete={handleDelete}
              isDeleting={isSaving}
              onOpen={openActivityDetail}
            />
          ) : (
            <ActivityItem
              key={activity.id}
              activity={activity}
              canDelete={canDelete}
              onDelete={handleDelete}
              isDeleting={isSaving}
              onOpen={openActivityDetail}
            />
          );
        })}
      </div>

      {/* Load more button */}
      {pagination && pagination.hasNextPage && (
        <div className="mt-4 text-center">
          <button
            onClick={handleLoadMore}
            disabled={isLoading}
            className="text-sm text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-400 font-medium disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />
                Loading...
              </>
            ) : (
              `Load more (${pagination.totalCount - activities.length} remaining)`
            )}
          </button>
        </div>
      )}

      {/* Activity count */}
      {pagination && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
          Showing {activities.length} of {pagination.totalCount} activities
        </p>
      )}
    </div>
  );
};
